/**
 * 提示词详情弹窗组件
 */

import React, { useState } from 'react';
import { Prompt, PromptService } from '../services/prompts';
import { getCategoryIcon } from '../utils/categories';

interface PromptDetailModalProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (prompt: Prompt) => void;
  onUse: (promptId: string) => void;
  onToggleFavorite: (promptId: string, isFavorite: boolean) => void;
  onClone?: (prompt: Prompt) => void;
}

const PromptDetailModal: React.FC<PromptDetailModalProps> = ({
  prompt,
  isOpen,
  onClose,
  onEdit,
  onUse,
  onToggleFavorite,
  onClone,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  if (!isOpen || !prompt) return null;

  // 复制提示词内容
  const handleCopy = async () => {
    const success = await PromptService.copyToClipboard(prompt.content);
    if (success) {
      setCopySuccess(true);
      onUse(prompt.id); // 增加使用次数
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // 切换收藏状态
  const handleToggleFavorite = () => {
    onToggleFavorite(prompt.id, !prompt.isFavorite);
  };

  // 格式化日期
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* 弹窗头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {prompt.title}
            </h2>
            <button
              onClick={handleToggleFavorite}
              className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${
                prompt.isFavorite ? 'text-yellow-500' : 'text-gray-400'
              }`}
              title={prompt.isFavorite ? '取消收藏' : '加入收藏'}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 弹窗内容 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* 描述 */}
          {prompt.description && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">描述</h3>
              <p className="text-gray-600">{prompt.description}</p>
            </div>
          )}

          {/* 分类和标签 */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 业务分类 */}
            {prompt.category && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">业务分类</h3>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getCategoryIcon(prompt.category)}</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    {prompt.category}
                  </span>
                </div>
              </div>
            )}

            {/* 功能标签 */}
            {prompt.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">功能标签</h3>
                <div className="flex flex-wrap gap-2">
                  {prompt.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 提示词内容 */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">提示词内容</h3>
            <div className="bg-gray-50 rounded-lg p-4 border">
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900">
                {prompt.content}
              </pre>
            </div>
          </div>

          {/* 统计信息 */}
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{prompt.viewCount || 0}</div>
              <div className="text-sm text-gray-500">查看次数</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{prompt.usageCount}</div>
              <div className="text-sm text-gray-500">使用次数</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{prompt.favoriteCount || 0}</div>
              <div className="text-sm text-gray-500">收藏数量</div>
            </div>
          </div>
        </div>

        {/* 弹窗底部操作栏 */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <span>创建于 {formatDate(prompt.createdAt)}</span>
          </div>

          <div className="flex items-center space-x-3">
            {/* 复制按钮 */}
            <button
              onClick={handleCopy}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                copySuccess
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
              }`}
            >
              {copySuccess ? (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  已复制到剪贴板
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  复制内容
                </div>
              )}
            </button>

            {/* 克隆按钮 */}
            {onClone && (
              <button
                onClick={() => {
                  onClone(prompt);
                  onClose();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                    <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                  </svg>
                  克隆提示词
                </div>
              </button>
            )}

            {/* 编辑按钮 */}
            <button
              onClick={() => {
                onEdit(prompt);
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                编辑提示词
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptDetailModal; 